import {
  call,
  put, takeEvery, takeLatest,
} from 'redux-saga/effects';
import { guaranteeHash } from '~/_shared/components/colorPicker/colorPicker.helpers';
import { PRESET_COLORS_PRIMARY } from '~/_shared/constants/colors.constants';
import { subtract } from '~/_shared/utils/collections/collections';
import { select } from '~/_shared/utils/saga/effects';
import type { PickAction } from '~/_shared/utils/types/action.type';
import type { BoundaryGroup } from '~/boundary/boundary.types';
import { BoundaryFill } from '~/boundary/settings/boundaryFill.type';
import { boundaryFillDefaultOpacity } from '~/boundary/settings/defaultBoundaryTerritoryStyles';
import { isDataLayersArtificialBoundaryTerritoryGroup } from '~/sidebar/sidebarApps/mapTools/dataLayersTool/dataLayersTool.helpers';
import type { BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import type { BoundaryGroupsAction } from '~/store/boundaryGroups/boundaryGroups.actions';
import { BOUNDARY_GROUPS_DELETE_SUCCESS } from '~/store/boundaryGroups/boundaryGroups.actionTypes';
import {
  boundaryGroupsSelector, checkBoundaryIsCustom,
} from '~/store/boundaryGroups/boundaryGroups.selectors';
import type { BoundaryItemsAction } from '~/store/boundaryItems/boundaryItems.action';
import { BOUNDARY_ITEMS_FETCHED_DATA } from '~/store/boundaryItems/boundaryItems.actionTypes';
import { mapBoundariesSelector } from '~/store/boundaryItems/boundaryItems.selectors';
import type { BoundaryStateItems } from '~/store/boundaryItems/boundaryItems.state';
import type { BoundaryTerritoryGroupsAction } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.action';
import {
  boundaryTerritoryGroupsRemoveCompleted, boundaryTerritoryGroupsRemoveRequest, boundaryTerritoryGroupsUpdateRequest,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import {
  BOUNDARY_TERRITORY_GROUP_CREATE_SUCCESS,
  BOUNDARY_TERRITORY_GROUP_FETCH_SUCCESS, BOUNDARY_TERRITORY_GROUP_REMOVE_COMPLETED,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionTypes';
import { BOUNDARY_TERRITORIES_MAYBE_REMOVED_ACTIONS } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.sagas';
import { boundaryTerritoryGroupsLoadedSelector } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import {
  boundaryToolBTGsSelector, useBoundaryToolBTGsSelector,
} from '~/store/boundaryTerritoryGroups/boundaryTool/boundaryToolBTGs.selectors';
import {
  mapSettingsBoundariesClearIsolatedBoundaries,
  mapSettingsBoundariesSetPrimaryBoundaryGroupId, mapSettingsBoundaryGroupOrderUpdated,
} from '~/store/mapSettings/boundaries/mapSettingsBoundaries.actionCreators';
import {
  mapSettingsBoundariesIsolatedBoundariesSelector, mapSettingsBoundaryOrderSelector,
} from '~/store/mapSettings/boundaries/mapSettingsBoundaries.selectors';
import {
  MAP_SETTINGS_FETCH_DATA_SUCCESS, MAP_SETTINGS_REMOVE_ACTIVE_ITEMS,
} from '~/store/mapSettings/data/mapSettingsData.actionTypes';
import { MAP_SETTINGS_READY_SET } from '~/store/mapSettings/ready/mapSettingsReady.actionTypes';
import { areMapSettingsReadySelector } from '~/store/mapSettings/ready/mapSettingsReady.selectors';
import { clearBoundaryLocationsFilter } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.actionCreators';
import type { BoundaryLocationsFiltersState } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.state';
import { boundaryLocationsFiltersStateSelector } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.stateSelectors';

export function* boundaryToolBoundaryTerritoryGroupsSagas() {
  yield takeLatest(MAP_SETTINGS_REMOVE_ACTIVE_ITEMS, removeAllTerritoryGroups);
  yield takeLatest(BOUNDARY_TERRITORY_GROUP_CREATE_SUCCESS, onBoundaryTerritoryGroupCreateSuccess);
  yield takeEvery(BOUNDARY_ITEMS_FETCHED_DATA, onBoundaryItemsFetchedData);
  yield takeEvery(BOUNDARY_GROUPS_DELETE_SUCCESS, removedBoundaryTerritoryGroupsForRemovedBoundaryGroups);
  yield takeLatest([
    BOUNDARY_TERRITORY_GROUP_FETCH_SUCCESS,
    MAP_SETTINGS_FETCH_DATA_SUCCESS,
    MAP_SETTINGS_READY_SET,
  ], ensureBoundaryGroupOrder);
  yield takeEvery(BOUNDARY_TERRITORY_GROUP_REMOVE_COMPLETED, updateBoundaryGroupOrderAfterRemoval);
  yield takeEvery(BOUNDARY_TERRITORIES_MAYBE_REMOVED_ACTIONS, ensureRemovedBTGConsistency);
}

function* removeAllTerritoryGroups() {
  const territoryGroups: ReadonlyArray<BoundaryTerritoryGroup> = yield select(
    boundaryToolBTGsSelector
  );

  const territoryGroupIds = territoryGroups.map(territoryGroup => territoryGroup.boundaryTerritoryGroupId);

  if (territoryGroupIds.length > 0) {
    yield put(boundaryTerritoryGroupsRemoveRequest(territoryGroupIds));
  }
}

function* updateBoundaryGroupOrderAfterRemoval(action: PickAction<BoundaryTerritoryGroupsAction, typeof BOUNDARY_TERRITORY_GROUP_REMOVE_COMPLETED>) {
  const allBTGs: ReadonlyArray<BoundaryTerritoryGroup> =
    yield select(boundaryToolBTGsSelector);
  const BGOrder: ReturnType<typeof mapSettingsBoundaryOrderSelector> =
    yield select<ReturnType<typeof mapSettingsBoundaryOrderSelector>>(mapSettingsBoundaryOrderSelector);
  const { boundaryTerritoryGroups } = action.payload;
  const removedBoundaryGroupIds = new Set(boundaryTerritoryGroups.map(({ boundaryGroupId }) => boundaryGroupId));

  for (const boundaryGroupId of removedBoundaryGroupIds) {
    const BTGsWithSameBoundaryGroupId = allBTGs.filter(btg => btg.boundaryGroupId === boundaryGroupId);

    if (BTGsWithSameBoundaryGroupId.length <= 0) {
      yield put(mapSettingsBoundaryGroupOrderUpdated(BGOrder.filter(id => id !== boundaryGroupId)));
    }
  }
}

function* ensureBoundaryGroupOrder() {
  const btgsReady: boolean = yield select(boundaryTerritoryGroupsLoadedSelector);
  const settingsReady: boolean = yield select(areMapSettingsReadySelector);

  if (!settingsReady || !btgsReady) {
    return;
  }

  const boundaryTerritoryGroups: ReadonlyArray<BoundaryTerritoryGroup> =
    yield select(boundaryToolBTGsSelector);
  const boundaryGroupOrder: ReturnType<typeof mapSettingsBoundaryOrderSelector> =
    yield select<ReturnType<typeof mapSettingsBoundaryOrderSelector>>(mapSettingsBoundaryOrderSelector);
  const unorderedBGsOrder = [...boundaryTerritoryGroups]
    .sort((btgA, btgB) => {
      // This sorting ensures that the boundary groups with the lowest ID are first (ASC)
      return btgA.boundaryTerritoryGroupId - btgB.boundaryTerritoryGroupId;
    })
    .reduce((acc, btg) => {
      if (!acc.includes(btg.boundaryGroupId) && !boundaryGroupOrder.includes(btg.boundaryGroupId)) {
        acc.unshift(btg.boundaryGroupId);
      }
      return acc;
    }, [] as BoundaryGroupId[]);

  const newBoundaryGroupOrder = [...unorderedBGsOrder, ...boundaryGroupOrder];

  yield put(mapSettingsBoundaryGroupOrderUpdated(newBoundaryGroupOrder));
}

function* onBoundaryTerritoryGroupCreateSuccess(action: PickAction<BoundaryTerritoryGroupsAction, typeof BOUNDARY_TERRITORY_GROUP_CREATE_SUCCESS>) {
  for (const boundaryTerritoryGroup of action.payload.boundaryTerritoryGroups) {
    if (isDataLayersArtificialBoundaryTerritoryGroup(boundaryTerritoryGroup)) {
      continue;
    }
    yield put(mapSettingsBoundariesSetPrimaryBoundaryGroupId(boundaryTerritoryGroup.boundaryGroupId));
    yield call(saveBoundaryItemsStyleIntoBoundaryTerritoryGroupSettings, boundaryTerritoryGroup.boundaryGroupId);
  }
}

function* onBoundaryItemsFetchedData(action: PickAction<BoundaryItemsAction, typeof BOUNDARY_ITEMS_FETCHED_DATA>) {
  yield call(saveBoundaryItemsStyleIntoBoundaryTerritoryGroupSettings, action.payload.boundaryGroupId);
}

// Items of custom boundary groups are loaded all at once so the actual update of B-T-G can only occur only once per boundary group
function* saveBoundaryItemsStyleIntoBoundaryTerritoryGroupSettings(boundaryGroupId: number) {
  const boundaryStateItems: BoundaryStateItems = yield select<BoundaryStateItems>(mapBoundariesSelector);
  const boundaryItems = Array.from(boundaryStateItems.get(boundaryGroupId)?.values() ?? []);

  const boundaryGroups: ReadonlyArray<BoundaryGroup> = yield select(boundaryGroupsSelector);
  const boundaryGroup = boundaryGroups.find(group => group.id === boundaryGroupId);

  if (!boundaryGroup || !boundaryItems || !checkBoundaryIsCustom(boundaryGroup)) {
    return;
  }

  const boundaryTerritoryGroups: ReadonlyArray<BoundaryTerritoryGroup> = yield select<ReadonlyArray<BoundaryTerritoryGroup>>(
    boundaryToolBTGsSelector
  );

  for (const boundaryTerritoryGroup of boundaryTerritoryGroups) {
    if (boundaryTerritoryGroup.boundaryGroupId !== boundaryGroupId) {
      continue;
    }

    let isUpdateRequired = false;

    const boundaryFillType = boundaryTerritoryGroup.settings.boundaryFillType ?? BoundaryFill.DefaultFill;
    if (boundaryFillType === BoundaryFill.NoTerritoryFill) {
      continue;
    }

    const newBoundaryStyle = new Map(boundaryTerritoryGroup.settings.boundaryStyle);
    for (const [index, item] of boundaryItems.entries()) {
      if (boundaryTerritoryGroup.settings.boundaryStyle.has(item.id)) {
        continue;
      }

      if (boundaryTerritoryGroup.settings.boundaryFillType === BoundaryFill.DefaultFill && item.settings.style) {
        newBoundaryStyle.set(item.id, item.settings.style);
        isUpdateRequired = true;
      }

      if (boundaryTerritoryGroup.settings.boundaryFillType === BoundaryFill.RandomizedFill) {
        const groupColor = PRESET_COLORS_PRIMARY[index % PRESET_COLORS_PRIMARY.length] ?? PRESET_COLORS_PRIMARY[0];
        newBoundaryStyle.set(item.id, {
          color: guaranteeHash(groupColor),
          opacity: boundaryFillDefaultOpacity * 100,
        });
        isUpdateRequired = true;
      }
    }

    if (!isUpdateRequired) {
      continue;
    }

    yield put(boundaryTerritoryGroupsUpdateRequest({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        boundaryStyle: newBoundaryStyle,
      },
    }, {
      fetchBoundaryDetails: false,

      // Reload BTG if it was updated in the meantime.
      // Probably another browser tab updated the initial color first.
      refetchOnConcurrencyError: true,
    }));
  }
}

function* removedBoundaryTerritoryGroupsForRemovedBoundaryGroups(action: PickAction<BoundaryGroupsAction, typeof BOUNDARY_GROUPS_DELETE_SUCCESS>) {
  const boundaryTerritoryGroups: ReadonlyArray<BoundaryTerritoryGroup> = yield select(
    useBoundaryToolBTGsSelector
  );

  for (const boundaryTerritoryGroup of boundaryTerritoryGroups) {
    if (!action.payload.groupIds.has(boundaryTerritoryGroup.boundaryGroupId)) {
      continue;
    }

    yield put(boundaryTerritoryGroupsRemoveCompleted([boundaryTerritoryGroup]));
  }
}

function* ensureRemovedBTGConsistency() {
  const boundaryTerritoryGroups: ReadonlyArray<BoundaryTerritoryGroup> = yield select(boundaryToolBTGsSelector);

  const locationFilters: BoundaryLocationsFiltersState = yield select(boundaryLocationsFiltersStateSelector);
  if (subtract(Object.keys(locationFilters), boundaryTerritoryGroups.map(btg => btg.boundaryGroupId.toString())).length !== 0) {
    yield put(clearBoundaryLocationsFilter());
  }

  const isolatedBoundaries: BoundaryLocationsFiltersState = yield select(mapSettingsBoundariesIsolatedBoundariesSelector);
  if (subtract(Object.keys(isolatedBoundaries), boundaryTerritoryGroups.map(btg => btg.boundaryGroupId.toString())).length !== 0) {
    yield put(mapSettingsBoundariesClearIsolatedBoundaries());
  }
}
